import { v4 as uuidv4 } from 'uuid';

import type { BusEventPayload, BusNavigationStepPosition } from '@typings/bus.ts';

import { DIRECTIONS_SEARCH_CONTROLS_ID, LANE_PICKER_VIEW_ID } from './constants.ts';

export type DirectionsPhase = 'editStops' | 'preview' | 'guidance';
export type DirectionsLeaveReason = 'pageHidden' | 'closed' | 'endRoute' | 'appReset' | 'navigatedAway';
type SessionFlag =
  | 'stopsModifiedAfterGuidance'
  | 'stopSelectedFromMap'
  | 'routeTypeSwitched'
  | 'routeUnavailable'
  | 'lanePickerShown'
  | 'reachedFinalStep';

type MetricsBus = {
  send: (name: string, payload: unknown) => Promise<unknown>;
  on: (name: string, listener: (payload: unknown) => void) => unknown;
  monitor: (name: string, listener: (payload: unknown) => void) => unknown;
  getFirst: (name: string, payload?: unknown) => Promise<unknown>;
};

const LEAVE_EVENTS: Record<DirectionsPhase, string> = {
  editStops: 'directions/leftEditStops',
  preview: 'directions/leftPreview',
  guidance: 'directions/leftGuidance',
};

// Shorter hidden periods are not reported; their time carries into the next period.
const MIN_HIDDEN_REPORT_MS = 1000;

const USER_MAP_CONTROLS = [
  'map/zoomIn',
  'map/zoomOut',
  'map/pan',
  'mapLevelSelector/selectLevel',
  'mapLevelSelector/selectBuilding',
];

const newPeriod = () => ({
  phaseMs: { editStops: 0, preview: 0, guidance: 0 },
  mapInteracted: { editStops: false, preview: false, guidance: false },
  steps: { forward: 0, back: 0, select: 0 },
});

export function createDirectionsMetrics(
  bus: MetricsBus,
  options: { directionsHeaderIds: string[]; getContext: () => Record<string, unknown> },
) {
  type Session = {
    id: string;
    sequence: number;
    phase: DirectionsPhase;
    visibleSince: number | null;
    guidanceStarted: boolean;
    noAccessibleRouteStops: Set<string>;
    flags: Record<SessionFlag, boolean>;
    period: ReturnType<typeof newPeriod>;
  };
  let session: Session | null = null;
  let latestLeave: { phase: DirectionsPhase; properties: Record<string, unknown> } | undefined;

  const flush = () => {
    if (session && session.visibleSince !== null) {
      const now = performance.now();
      session.period.phaseMs[session.phase] += now - session.visibleSince;
      session.visibleSince = now;
    }
  };

  const periodProperties = ({ id, period, flags }: Session) => {
    const { phaseMs, mapInteracted, steps } = period;
    const editStopsMs = Math.round(phaseMs.editStops);
    const previewMs = Math.round(phaseMs.preview);
    const guidanceMs = Math.round(phaseMs.guidance);
    return {
      url: window.location.href,
      directionsSessionId: id,
      editStopsMs,
      previewMs,
      guidanceMs,
      totalMs: editStopsMs + previewMs + guidanceMs,
      mapInteractedEditStops: mapInteracted.editStops,
      mapInteractedPreview: mapInteracted.preview,
      mapInteractedGuidance: mapInteracted.guidance,
      stepForwardCount: steps.forward,
      stepBackCount: steps.back,
      stepSelectCount: steps.select,
      ...flags,
      ...options.getContext(),
    };
  };

  const report = (reason: DirectionsLeaveReason) => {
    if (!session) {
      return;
    }
    const properties = { ...periodProperties(session), sequence: session.sequence++, reason };
    latestLeave = { phase: session.phase, properties };
    void bus.send('appInsights/log', { name: LEAVE_EVENTS[session.phase], properties }).catch(() => {});
    session.period = newPeriod();
  };

  const pause = () => {
    if (!session || session.visibleSince === null) {
      return;
    }
    flush();
    session.visibleSince = null;
    const { editStops, preview, guidance } = session.period.phaseMs;
    if (editStops + preview + guidance >= MIN_HIDDEN_REPORT_MS) {
      report('pageHidden');
    }
  };

  const onVisibilityChange = () => {
    if (!session) {
      return;
    }
    if (document.hidden) {
      pause();
    } else if (session.visibleSince === null) {
      session.visibleSince = performance.now();
    }
  };

  const leave = (reason: DirectionsLeaveReason) => {
    if (!session) {
      return;
    }
    flush();
    report(reason);
    session = null;
  };

  const interact = () => {
    if (session && session.visibleSince !== null) {
      session.period.mapInteracted[session.phase] = true;
    }
  };

  const enter = (phase: DirectionsPhase) => {
    if (!session) {
      session = {
        id: uuidv4(),
        sequence: 0,
        phase,
        visibleSince: document.hidden ? null : performance.now(),
        guidanceStarted: phase === 'guidance',
        noAccessibleRouteStops: new Set(),
        flags: {
          stopsModifiedAfterGuidance: false,
          stopSelectedFromMap: false,
          routeTypeSwitched: false,
          routeUnavailable: false,
          lanePickerShown: false,
          reachedFinalStep: false,
        },
        period: newPeriod(),
      };
      return;
    }
    flush();
    session.guidanceStarted ||= phase === 'guidance';
    session.phase = phase;
  };

  const markFinalStep = ({ stopIndex, stepIndex, stepCounts }: BusNavigationStepPosition) => {
    if (
      session &&
      stepCounts.length > 0 &&
      stopIndex === stepCounts.length - 1 &&
      stepIndex === stepCounts[stopIndex] - 1
    ) {
      session.flags.reachedFinalStep = true;
    }
  };
  const modifyStops = () => {
    if (session?.guidanceStarted) {
      session.flags.stopsModifiedAfterGuidance = true;
    }
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', pause);
  window.addEventListener('pageshow', onVisibilityChange);

  bus.monitor('map/userMoveStart', interact);
  for (const name of USER_MAP_CONTROLS) {
    bus.monitor(name, payload => {
      if ((payload as { userInitiated?: boolean } | undefined)?.userInitiated) {
        interact();
      }
    });
  }
  bus.on('app/reset', () => leave('appReset'));
  // Feedback gets the open session's current period, or else the most recent leave event.
  bus.on('navigation/getFeedbackMetrics', () => {
    if (session) {
      flush();
      return { ...periodProperties(session), phase: session.phase, inProgress: true };
    }
    return latestLeave ? { ...latestLeave.properties, phase: latestLeave.phase, inProgress: false } : {};
  });
  bus.on('navigation/closeDirections', payload => {
    const { reason } = payload as BusEventPayload<'navigation/closeDirections'>;
    leave(reason);
  });
  bus.monitor('navigation/stepChanged', payload => {
    if (!session) {
      return;
    }
    const step = payload as BusEventPayload<'navigation/stepChanged'>;
    if (step.kind !== 'back') {
      enter('guidance');
    }
    if (session) {
      session.period.steps[step.kind]++;
      markFinalStep(step);
    }
  });
  bus.monitor('navigation/guidanceChanged', payload => {
    if (!session) {
      return;
    }
    const guidance = payload as BusEventPayload<'navigation/guidanceChanged'>;
    enter(guidance.active ? 'guidance' : 'preview');
    if (guidance.active) {
      markFinalStep(guidance);
    }
  });
  bus.monitor('navigation/editStops', modifyStops);
  bus.monitor('navigation/stopsChanged', payload => {
    if ((payload as BusEventPayload<'navigation/stopsChanged'>).userInitiated) {
      modifyStops();
    }
  });
  bus.monitor('navigation/routeTypeChanged', payload => {
    const { previous, current, userInitiated } = payload as BusEventPayload<'navigation/routeTypeChanged'>;
    if (session && userInitiated && previous !== current) {
      session.flags.routeTypeSwitched = true;
    }
  });
  bus.monitor('navigation/routeUnavailable', () => {
    if (session) {
      session.flags.routeUnavailable = true;
    }
  });
  bus.monitor('navigation/noAccessibleRoute', payload => {
    const { endpointIds, accessibleRequested } = payload as BusEventPayload<'navigation/noAccessibleRoute'>;
    // Location endpoints share one key so blue-dot reroutes do not repeat the event.
    const stops = endpointIds.map(id => (id !== null && typeof id === 'object' ? 'location' : String(id))).join('>');
    if (!session || session.noAccessibleRouteStops.has(stops)) {
      return;
    }
    session.noAccessibleRouteStops.add(stops);
    void bus
      .send('appInsights/log', {
        name: 'directions/noAccessibleRoute',
        properties: {
          url: window.location.href,
          directionsSessionId: session.id,
          phase: session.phase,
          ...options.getContext(),
          accessibleRequested,
        },
      })
      .catch(() => {});
  });
  bus.monitor('navigation/show', () => enter('editStops'));
  bus.monitor('event/directions', () => {
    if (session?.phase !== 'guidance') {
      enter('preview');
    }
  });
  bus.monitor('map/poiClicked', payload => {
    const { poi } = payload as { poi: { isNavigable?: boolean } };
    const sessionId = session?.id;
    if (!sessionId || !poi.isNavigable) {
      return;
    }
    void bus
      .send('layers/isShowing', { id: DIRECTIONS_SEARCH_CONTROLS_ID })
      .then(results => {
        if (session?.id === sessionId && (results as unknown[])[0]) {
          session.flags.stopSelectedFromMap = true;
        }
      })
      .catch(() => {});
  });
  bus.monitor('app/unmount', () => leave('appReset'));
  bus.monitor('layers/hideWithLayoutId', layoutId => {
    if (layoutId === 'headerOnline') {
      leave('navigatedAway');
    }
  });
  bus.monitor('layers/show', payload => {
    const { id } = payload as { id: string };
    if (id === LANE_PICKER_VIEW_ID && session) {
      session.flags.lanePickerShown = true;
    }
    const sessionId = session?.id;
    if (!sessionId || options.directionsHeaderIds.includes(id)) {
      return;
    }
    void bus
      .getFirst('layers/getLayers')
      .then(layers => {
        const layer = (layers as Record<string, { layoutId?: string; isOverlay?: boolean }> | undefined)?.[id];
        if (session?.id === sessionId && layer?.layoutId === 'headerOnline' && !layer.isOverlay) {
          leave('navigatedAway');
        }
      })
      .catch(() => {});
  });

  return {
    get sessionId() {
      return session?.id;
    },
    get phase() {
      return session?.phase;
    },
  };
}
